import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../hooks/useTheme';
import {
  useNotifications,
  NOTIFICATION_ROUTES,
  getNotificationIcon,
  getPriorityColor,
  formatTimeAgo,
} from '../hooks/useNotifications';

export default function Notifications() {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { notifications, unreadCount, markAsRead, markAllAsRead, dismissNotification } = useNotifications();

  const handleClick = async (notification) => {
    await markAsRead(notification);
    const route = NOTIFICATION_ROUTES[notification.type];
    if (route) {
      navigate(route);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-slate-100' : 'text-gray-900'}`}>
            Notifications
          </h1>
          {unreadCount > 0 && (
            <p className={`text-sm mt-1 ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
              {unreadCount} unread
            </p>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={markAllAsRead}
            className={`text-sm font-medium ${isDarkMode ? 'text-primary-400 hover:text-primary-300' : 'text-blue-600 hover:text-blue-800'}`}
          >
            Mark all read
          </button>
        )}
      </div>

      {/* Notifications List */}
      <div className={`rounded-lg border overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
        {notifications.length === 0 ? (
          <div className={`p-12 text-center ${isDarkMode ? 'text-slate-400' : 'text-gray-500'}`}>
            <svg className={`w-12 h-12 mx-auto mb-4 ${isDarkMode ? 'text-slate-600' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
            <p>No notifications yet</p>
          </div>
        ) : (
          notifications.map((notification) => (
            <div
              key={notification.id}
              onClick={() => handleClick(notification)}
              className={`p-4 border-b border-l-4 cursor-pointer transition-colors duration-150 ${
                !notification.isRead
                  ? `${getPriorityColor(notification.priority, isDarkMode)} ${isDarkMode ? 'border-b-slate-700 bg-blue-900/20 hover:bg-slate-700/50' : 'border-b-gray-100 bg-blue-50 hover:bg-gray-50'}`
                  : isDarkMode ? 'border-l-slate-700 border-b-slate-700/50 bg-transparent hover:bg-slate-700/40' : 'border-l-gray-200 border-b-gray-100 bg-white hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start space-x-3">
                {/* Icon */}
                <span className="text-lg flex-shrink-0 mt-0.5">
                  {getNotificationIcon(notification.type)}
                </span>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h4 className={`text-sm font-medium ${
                      !notification.isRead
                        ? isDarkMode ? 'text-slate-100' : 'text-gray-900'
                        : isDarkMode ? 'text-slate-300' : 'text-gray-700'
                    }`}>
                      {notification.title}
                    </h4>
                    <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                      {!notification.isRead && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full" />
                      )}
                      <button
                        type="button"
                        aria-label="dismiss notification"
                        onClick={(event) => {
                          event.stopPropagation();
                          dismissNotification(notification);
                        }}
                        className={`rounded p-1 text-sm leading-none transition-colors ${
                          isDarkMode ? 'text-slate-500 hover:text-slate-200 hover:bg-slate-700' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                  <p className={`text-sm mt-1 ${
                    !notification.isRead
                      ? isDarkMode ? 'text-slate-300' : 'text-gray-700'
                      : isDarkMode ? 'text-slate-400' : 'text-gray-500'
                  }`}>
                    {notification.message}
                  </p>
                  <p className={`text-xs mt-2 ${isDarkMode ? 'text-slate-500' : 'text-gray-400'}`}>
                    {formatTimeAgo(notification.createdAt)}
                  </p>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
